import type { ComponentProps, ReactNode } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderProps,
  BorderRadiusProps,
  DisplayProps,
  HeightProps,
  JustifyContentProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderRadius,
  display,
  height,
  justifyContent,
  themeGet,
} from 'styled-system';

import { GrowthbookFlag } from '../../constants/feature-flags';
import { useFeatureFlagIsOn } from '../../hooks/feature-flags';
import { focusOutlineCSS } from '../../theme/styles';
import type { OutlineProps } from '../../theme/system-types';
import type { CursorProps, OutlineColorProps } from '../../theme/system-utilities';
import { cursor, outline, outlineColor } from '../../theme/system-utilities';
import { InfoIcon, Label, labelStyles, LabelWrapper } from '../DiscountLabels/label-primitives';
import { CaptionRegularCR1 } from '../Typography';

const ICON_SIZE = 24;
const TEXT_COLOR = 'sys.neutral.text.strong';
const TEXT_COLOR_V2 = 'sys.neutral.text.strong';

const BaseLabelWrapper = styled(LabelWrapper)<
  BorderRadiusProps & OutlineColorProps & OutlineProps & BackgroundColorProps
>`
  ${borderRadius};
  ${outline};
  ${outlineColor};
  ${backgroundColor};

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
  }

  &:active {
    background-color: ${themeGet('colors.sys.neutral.background.active')};
  }
`;

const baseLabelWrapperStyles: ComponentProps<typeof BaseLabelWrapper> = {
  borderRadius: 'small',
  outline: '1px solid',
  outlineColor: 'sys.neutral.border.weak',
  backgroundColor: 'sys.neutral.background.default',
};

const BaseLabelStyled = styled(Label)`
  ${focusOutlineCSS};
`;

const BaseLabelV2Styled = styled(CaptionRegularCR1)<
  BorderProps &
    BackgroundColorProps &
    CursorProps &
    HeightProps &
    DisplayProps &
    AlignItemsProps &
    JustifyContentProps &
    BorderRadiusProps
>`
  ${border};
  ${backgroundColor};
  ${cursor};
  ${height};
  ${display};
  ${alignItems};
  ${justifyContent};
  ${borderRadius};
  ${focusOutlineCSS};
`;

interface BaseLabelProps {
  children: ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  className?: string;
  testId?: string;
  ariaLabel?: string;
  iconTestId?: string;
}

export const BaseLabel = ({
  children,
  onClick,
  className,
  testId,
  ariaLabel,
  iconTestId,
}: BaseLabelProps) => {
  const isV2 = useFeatureFlagIsOn(GrowthbookFlag.ProductCardV2);
  const LabelComponent = isV2 ? BaseLabelV2Styled : BaseLabelStyled;

  return (
    <BaseLabelWrapper {...baseLabelWrapperStyles} className={className}>
      <LabelComponent
        {...labelStyles}
        as="button"
        border="none"
        bg="transparent"
        color={isV2 ? TEXT_COLOR_V2 : TEXT_COLOR}
        cursor="pointer"
        fontWeight="normal"
        data-testid={testId}
        aria-label={ariaLabel}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
        }}
      >
        {children}
        <InfoIcon
          dataTestId={iconTestId}
          size={ICON_SIZE}
          name="info"
          marginRight="-5px"
          fill="sys.neutral.icon.strong"
        />
      </LabelComponent>
    </BaseLabelWrapper>
  );
};
